/* eslint-disable react-hooks/exhaustive-deps */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Platform} from 'react-native';

import {
  DELIVERED,
  FIRST_WEEK,
  FIRST_WEEK_SCREEN,
  PRE_JOURNEY,
  PRE_JOURNEY_SCREEN,
  WEEKLY_CHECK_INS,
  WEEKLY_CHECK_INS_SCREEN,
} from '/enum/clientJourneyTasks.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {enableLayoutAnimation, safeLayoutAnimation} from '/util/animation.util';

import {isTabCompleted} from './ClientJourneyTask.util';
import {FirstWeek} from './FirstWeek';
import {PreJourney} from './PreJourney';
import {WeeklyCheckIns} from './WeeklyCheckIns';

const FIRST_WEEK_TAB_HEIGHT_DELIVERED = Platform.OS === 'web' ? 850 : 800;
const FIRST_WEEK_TAB_HEIGHT = Platform.OS === 'web' ? 750 : 700;
const WEEKLY_CHECKINS_TAB_HEIGHT = Platform.OS === 'web' ? 200 : 250;
const PRE_JOURNEY_TAB_HEIGHT = Platform.OS === 'web' ? 350 : 300;

export const useClientJourney = ({
  hasFirstWeekTasks,
  hasFirstOrder,
  hasAllTasks,
  clientId,
  onOrderPress,
  getClientJourneyTasks,
  getClientJourneyAllTasks,
  currentScreen,
  firstOrderShippingStatus,
  hasPreJourneyTasks,
  hasWeeklyCheckInsTasks,
  firstOrderId,
  getFirstOrder,
  onShowTaskDetail,
  isEmptyAllTasks,
  remoteCodeConfig,
  clientJourneyTaskData,
}: any) => {
  const {ts} = useTranslation('screen.clients.section.clientJourney');

  const getCurrentInitialRouteName = () => {
    if (!isTabCompleted(clientJourneyTaskData?.preJourney)) {
      return PRE_JOURNEY_SCREEN;
    } else if (!isTabCompleted(clientJourneyTaskData?.firstWeek)) {
      return FIRST_WEEK_SCREEN;
    } else {
      return WEEKLY_CHECK_INS_SCREEN;
    }
  };

  const activeScreen = currentScreen || getCurrentInitialRouteName();

  const getTabHeightForScreen = (screenName: string) => {
    let tabHeight = 0;
    if (screenName === PRE_JOURNEY_SCREEN) {
      tabHeight = PRE_JOURNEY_TAB_HEIGHT;
    }
    if (screenName === FIRST_WEEK_SCREEN) {
      if (firstOrderShippingStatus === DELIVERED) {
        tabHeight = FIRST_WEEK_TAB_HEIGHT_DELIVERED;
      } else {
        tabHeight = FIRST_WEEK_TAB_HEIGHT;
      }
    }
    if (screenName === WEEKLY_CHECK_INS_SCREEN) {
      tabHeight = WEEKLY_CHECKINS_TAB_HEIGHT;
    }
    return tabHeight;
  };

  const [clientJourneyExpanded, setClientJourneyExpanded] = useState(true);
  const [tabHeight, setTabHeight] = useState(0);

  const timer = remoteCodeConfig?.clientJourneyTimer * 1000;

  const toggleExpand = () => {
    safeLayoutAnimation();
    setClientJourneyExpanded(!clientJourneyExpanded);
  };

  const getTabComponent = useCallback(
    (Component: any) => () =>
      (
        <Component
          clientId={clientId}
          onOrderPress={onOrderPress}
          onShowTaskDetail={onShowTaskDetail}
          orderId={firstOrderId}
          clientJourneyTimer={timer}
        />
      ),
    [],
  );
  const memoizedTabs = useMemo(() => {
    let tabs: any = [];

    if (!isEmptyAllTasks) {
      tabs = [
        {
          name: PRE_JOURNEY_SCREEN,
          label: ts('PreJourneyLabel', PRE_JOURNEY_SCREEN),
          component: getTabComponent(PreJourney),
          onTabPress: () => {
            setTabHeight(getTabHeightForScreen(PRE_JOURNEY_SCREEN));
          },
        },
        {
          name: FIRST_WEEK_SCREEN,
          label: ts('FirstWeekLabel', FIRST_WEEK_SCREEN),
          component: getTabComponent(FirstWeek),
          onTabPress: () => {
            setTabHeight(getTabHeightForScreen(FIRST_WEEK_SCREEN));
          },
        },
        {
          name: WEEKLY_CHECK_INS_SCREEN,
          label: ts('WeeklyCheckInsLabel', WEEKLY_CHECK_INS_SCREEN),
          component: getTabComponent(WeeklyCheckIns),
          onTabPress: () => {
            setTabHeight(getTabHeightForScreen(WEEKLY_CHECK_INS_SCREEN));
          },
        },
      ];
    }
    return tabs;
  }, [isEmptyAllTasks, firstOrderShippingStatus]);

  useEffect(() => {
    if (!hasAllTasks) {
      getClientJourneyAllTasks();
    }
    if (!hasFirstWeekTasks) {
      getClientJourneyTasks({type: FIRST_WEEK});
    }
    if (!hasPreJourneyTasks) {
      getClientJourneyTasks({type: PRE_JOURNEY});
    }
    if (!hasWeeklyCheckInsTasks) {
      getClientJourneyTasks({type: WEEKLY_CHECK_INS});
    }
  }, []);

  useEffect(() => {
    if (
      activeScreen === PRE_JOURNEY_SCREEN ||
      activeScreen === FIRST_WEEK_SCREEN ||
      activeScreen === WEEKLY_CHECK_INS_SCREEN
    ) {
      setTabHeight(getTabHeightForScreen(activeScreen));
    }
  }, [currentScreen, hasFirstOrder, firstOrderShippingStatus]);

  useEffect(() => {
    if (firstOrderId && !hasFirstOrder) {
      getFirstOrder({firstOrderId});
    }
  }, [firstOrderId]);

  useEffect(() => {
    enableLayoutAnimation();
  }, []);

  return {
    clientJourneyExpanded,
    toggleExpand,
    tabs: memoizedTabs,
    currentInitialRouteName: getCurrentInitialRouteName(),
    tabHeight,
    ts,
  };
};
